/**
 * Explorer tree — the Windows-Explorer style column that sits between the module menu and the
 * page content (Courses, Question bank). Resizable, collapsible nodes, search, persisted expansion.
 *
 *   <ExplorerLayout explorer={{ title, nodes, selectedKey, onSelect, loading, storageKey, footer }}>
 *     …page content (a <Page> with a grid, or a <RecordPage>)…
 *   </ExplorerLayout>
 *
 * node: { key, label, icon, iconClass, children: [], badge, muted, data, defaultOpen }
 * onSelect(node) is called with the clicked node (or null when the current selection is clicked again
 * — "deselect"). Use `expandTo(key)` from the ref to open the path to a node.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useExplorerState } from './ExplorerContext';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../ui/resizable';
import { CaretRight, CaretDown, MagnifyingGlass, ArrowsInLineVertical, ArrowsOutLineVertical, TreeStructure, DotsThreeVertical } from '@phosphor-icons/react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';

/** 3-dot menu on a tree row. items: [{ key, label, icon, onClick, disabled, danger, separator }] */
function RowMenu({ items, selected }) {
  if (!items || items.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={`w-6 h-6 ml-1 flex items-center justify-center rounded flex-shrink-0 ${selected ? 'text-white/90 hover:bg-white/20 opacity-100' : 'text-muted-foreground opacity-0 group-hover:opacity-100 hover:bg-muted'} data-[state=open]:opacity-100`}
          title="Actions"
          aria-label="Actions"
          data-testid="explorer-row-menu"
        >
          <DotsThreeVertical className="w-4 h-4" weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56" onClick={(e) => e.stopPropagation()}>
        {items.map((it, i) => it.separator ? <DropdownMenuSeparator key={`sep-${i}`} /> : (
          <DropdownMenuItem key={it.key || it.label} disabled={it.disabled} onClick={(e) => { e.stopPropagation(); it.onClick?.(); }} className={it.danger ? 'text-destructive focus:text-destructive' : ''} data-testid={`explorer-menu-${it.key || i}`}>
            {it.icon && <it.icon className="w-4 h-4 mr-2" />}{it.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function useExpanded(storageKey, defaults) {
  const [open, setOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(`lms_explorer_${storageKey}`);
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    return new Set(defaults);
  });
  useEffect(() => {
    try { localStorage.setItem(`lms_explorer_${storageKey}`, JSON.stringify([...open])); } catch { /* ignore */ }
  }, [open, storageKey]);
  return [open, setOpen];
}

function pathTo(nodes, key, trail = []) {
  for (const n of nodes) {
    if (n.key === key) return [...trail, n.key];
    if (n.children?.length) {
      const p = pathTo(n.children, key, [...trail, n.key]);
      if (p) return p;
    }
  }
  return null;
}

function allKeys(nodes, out = []) {
  nodes.forEach((n) => { if (n.children?.length) { out.push(n.key); allKeys(n.children, out); } });
  return out;
}

function filterTree(nodes, q) {
  if (!q) return nodes;
  const out = [];
  for (const n of nodes) {
    const kids = n.children?.length ? filterTree(n.children, q) : [];
    if (n.label.toLowerCase().includes(q) || kids.length) out.push({ ...n, children: kids, _forceOpen: kids.length > 0 });
  }
  return out;
}

function TreeRow({ node, depth, open, toggle, selectedKey, onSelect, menuFor }) {
  const hasKids = node.children?.length > 0;
  const isOpen = node._forceOpen || open.has(node.key);
  const selected = node.key === selectedKey;
  const Icon = node.icon;
  return (
    <li>
      <div
        role="treeitem"
        aria-expanded={hasKids ? isOpen : undefined}
        aria-selected={selected}
        className={`group flex items-center h-7 pr-2 rounded cursor-pointer select-none text-[13px] ${selected ? 'bg-shell-active text-white' : 'hover:bg-muted/70'} ${node.muted && !selected ? 'text-muted-foreground' : ''}`}
        style={{ paddingLeft: 6 + depth * 14 }}
        onClick={() => onSelect(selected ? null : node)}
        onDoubleClick={() => hasKids && toggle(node.key)}
        data-testid={`explorer-node-${node.key}`}
        title={node.title || node.label}
      >
        <button
          type="button"
          className={`w-4 h-4 mr-0.5 flex items-center justify-center flex-shrink-0 ${hasKids ? (selected ? 'text-white/80' : 'text-muted-foreground hover:text-foreground') : 'invisible'}`}
          onClick={(e) => { e.stopPropagation(); if (hasKids) toggle(node.key); }}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
        >
          {isOpen ? <CaretDown className="w-3 h-3" weight="bold" /> : <CaretRight className="w-3 h-3" weight="bold" />}
        </button>
        {Icon && <Icon className={`w-4 h-4 mr-1.5 flex-shrink-0 ${selected ? 'text-white' : node.iconClass || 'text-shell-primary'}`} weight={selected ? 'fill' : 'duotone'} />}
        <span className="truncate flex-1">{node.label}</span>
        {node.badge !== undefined && node.badge !== null && (
          <span className={`ml-2 text-[10px] font-mono tabular-nums rounded px-1 ${selected ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`}>{node.badge}</span>
        )}
        {menuFor && <RowMenu items={menuFor(node)} selected={selected} />}
      </div>
      {hasKids && isOpen && (
        <ul role="group">
          {node.children.map((c) => <TreeRow key={c.key} node={c} depth={depth + 1} open={open} toggle={toggle} selectedKey={selectedKey} onSelect={onSelect} menuFor={menuFor} />)}
        </ul>
      )}
    </li>
  );
}

export function ExplorerTree({ title = 'Explorer', nodes = [], selectedKey, onSelect, loading, storageKey = 'default', footer, emptyText = 'Nothing here yet', icon: TitleIcon = TreeStructure, defaultOpenKeys = [], menuFor, headerActions }) {
  const [open, setOpen] = useExpanded(storageKey, defaultOpenKeys);
  const [query, setQuery] = useState('');
  const toggle = (k) => setOpen((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const q = query.trim().toLowerCase();
  const shown = useMemo(() => filterTree(nodes, q), [nodes, q]);

  // keep the selected node visible
  useEffect(() => {
    if (!selectedKey) return;
    const p = pathTo(nodes, selectedKey);
    if (p) setOpen((s) => { const n = new Set(s); p.slice(0, -1).forEach((k) => n.add(k)); return n; });
  }, [selectedKey, nodes, setOpen]);

  return (
    <div className="h-full flex flex-col bg-white border-r border-border" data-testid="explorer-tree">
      <div className="flex items-center gap-1.5 h-9 px-2.5 border-b border-border bg-muted/30 flex-shrink-0">
        <TitleIcon className="w-4 h-4 text-shell-primary" weight="duotone" />
        <span className="text-[13px] font-semibold flex-1 truncate">{title}</span>
        {headerActions}
        <button type="button" className="w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground" title="Expand all" onClick={() => setOpen(new Set(allKeys(nodes)))}><ArrowsOutLineVertical className="w-3.5 h-3.5" /></button>
        <button type="button" className="w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground" title="Collapse all" onClick={() => setOpen(new Set())}><ArrowsInLineVertical className="w-3.5 h-3.5" /></button>
      </div>
      <div className="px-2 py-1.5 border-b border-border flex-shrink-0">
        <div className="relative">
          <MagnifyingGlass className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find in tree" className="w-full h-7 pl-7 pr-2 rounded border border-input bg-muted/30 text-xs outline-none focus:bg-white focus:border-shell-primary" data-testid="explorer-search" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-1 px-1">
        {loading && nodes.length === 0 ? (
          <p className="px-2 py-4 text-xs text-muted-foreground animate-pulse">Loading…</p>
        ) : shown.length === 0 ? (
          <p className="px-2 py-4 text-xs text-muted-foreground">{q ? 'No matches' : emptyText}</p>
        ) : (
          <ul role="tree">
            {shown.map((n) => <TreeRow key={n.key} node={n} depth={0} open={open} toggle={toggle} selectedKey={selectedKey} onSelect={onSelect} menuFor={menuFor} />)}
          </ul>
        )}
      </div>
      {footer && <div className="border-t border-border px-2.5 py-1.5 text-[11px] text-muted-foreground flex-shrink-0">{footer}</div>}
    </div>
  );
}

export default function ExplorerLayout({ explorer, children, width = 22 }) {
  const { open, register } = useExplorerState();
  const present = !!explorer && !explorer.hidden;
  useEffect(() => (present ? register() : undefined), [present, register]);
  if (!present) return children;
  if (!open) return <div className="h-full overflow-y-auto">{children}</div>;
  return (
    <ResizablePanelGroup direction="horizontal" autoSaveId={`explorer-${explorer.storageKey || 'default'}`} className="flex-1 min-h-0 !h-full items-stretch">
      <ResizablePanel defaultSize={width} minSize={12} maxSize={45} className="min-w-0 h-full">
        <ExplorerTree {...explorer} />
      </ResizablePanel>
      <ResizableHandle withHandle className="bg-border/80 hover:bg-shell-active/60 w-1.5" />
      <ResizablePanel defaultSize={100 - width} minSize={40} className="min-w-0 h-full">
        {/* react-resizable-panels forces overflow:hidden on the panel itself — the page scrolls inside this wrapper */}
        <div className="h-full overflow-y-auto overflow-x-hidden" data-testid="explorer-content-scroll">{children}</div>
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
